// frontend/src/pages/PetServiceTablePage.js
import React, { useState, useEffect, useRef } from 'react';
import useApi from '../hooks/useApi';
import { Plus, Edit, Trash2, Save, X, Loader2, Upload, Download, FileSpreadsheet, Search, DollarSign } from 'lucide-react';

const PetServiceTablePage = ({ selectedCurrency, formatCurrency, allCurrencies }) => {
    const { apiFetch, permissions, userProfile } = useApi(); 
    const [services, setServices] = useState([]);
    const [filteredServices, setFilteredServices] = useState([]); 
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    // Estado do Modal
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingService, setEditingService] = useState(null);
    
    const [formData, setFormData] = useState({ 
        name: '', 
        type: 'Banho', 
        description: '',
        prices: {} // Guardará { BRL: 30, USD: 5 }
    });
    const [saving, setSaving] = useState(false);

    // Referência para o input de arquivo (upload)
    const fileInputRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    // Permissões
    const canCreate = permissions?.service_table_create || userProfile?.is_super_user;
    const canEdit = permissions?.service_table_edit || userProfile?.is_super_user;
    const canDelete = permissions?.service_table_delete || userProfile?.is_super_user;

    // Carregar dados
    const fetchServices = async () => {
        setLoading(true);
        try {
            const data = await apiFetch('/pet-services');
            setServices(data || []);
            setFilteredServices(data || []);
            setError('');
        } catch (err) {
            console.error("Erro ao buscar serviços pet:", err);
            setError("Erro ao carregar serviços pet.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchServices();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Filtro de busca local
    useEffect(() => {
        if (!searchTerm) {
            setFilteredServices(services);
        } else {
            const term = searchTerm.toLowerCase();
            const filtered = services.filter(s => 
                s.name.toLowerCase().includes(term) ||
                (s.type && s.type.toLowerCase().includes(term)) ||
                (s.description && s.description.toLowerCase().includes(term))
            );
            setFilteredServices(filtered);
        }
    }, [searchTerm, services]);

    // Handlers do Modal
    const handleOpenModal = (service = null) => {
        setEditingService(service);
        
        // Garante que todas as moedas do sistema tenham um campo, mesmo que vazio
        const pricesConfig = {};
        allCurrencies.forEach(c => {
            pricesConfig[c.code] = service?.prices?.[c.code] || '';
        });

        setFormData(service ? { 
            name: service.name, 
            type: service.type || 'Banho',
            description: service.description || '',
            prices: pricesConfig
        } : { 
            name: '', 
            type: 'Banho',
            description: '',
            prices: pricesConfig
        });
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingService(null);
    };

    const handlePriceChange = (code, value) => {
        setFormData(prev => ({
            ...prev,
            prices: {
                ...prev.prices,
                [code]: value
            }
        }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (editingService) {
                await apiFetch(`/pet-services/${editingService.id}`, {
                    method: 'PUT',
                    body: formData
                });
            } else {
                await apiFetch('/pet-services', {
                    method: 'POST',
                    body: formData
                });
            }
            handleCloseModal();
            fetchServices(); 
        } catch (err) {
            alert("Erro ao salvar: " + err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Tem certeza que deseja excluir este serviço?")) {
            try {
                await apiFetch(`/pet-services/${id}`, {
                    method: 'DELETE'
                });
                fetchServices();
            } catch (err) {
                alert("Erro ao excluir: " + err.message);
            }
        }
    };

    const handleExport = async () => {
        try {
            const token = localStorage.getItem('token'); 
            const response = await fetch('/api/pet-services/export', { 
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) throw new Error('Falha no download');

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'Tabela_de_Servicos_Pet_Completa.xlsx';
            document.body.appendChild(a);
            a.click();
            a.remove();
        } catch (err) {
            alert("Erro ao exportar: " + err.message);
        }
    };

    const handleImportClick = () => {
        fileInputRef.current.click();
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);

        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/pet-services/import', { 
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formDataUpload
            });

            const result = await response.json();
            if (!response.ok) throw new Error(result.error || 'Falha na importação');

            alert(result.message);
            fetchServices(); 
        } catch (err) {
            alert("Erro na importação: " + err.message);
        } finally {
            setUploading(false);
            e.target.value = ''; 
        }
    };

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800">Tabela de Serviços Pet</h1>
                    <p className="text-sm text-gray-600">Gerencie os valores e serviços da clínica em múltiplas moedas.</p>
                </div>
                
                <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
                    <div className="relative w-full md:w-64">
                        <input 
                            type="text" 
                            placeholder="Buscar serviço..." 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                        <Search size={16} className="absolute left-3 top-2.5 text-gray-400" />
                    </div>

                    <button 
                        onClick={handleExport}
                        className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 transition-colors"
                        title="Baixar planilha"
                    >
                        <Download size={16} /> <span className="hidden sm:inline">Exportar Tudo</span>
                    </button>

                    {canCreate && (
                        <>
                            <input 
                                type="file" 
                                ref={fileInputRef} 
                                onChange={handleFileChange} 
                                accept=".xlsx, .xls" 
                                className="hidden" 
                            />
                            <button 
                                onClick={handleImportClick}
                                disabled={uploading}
                                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 disabled:opacity-50 transition-colors"
                                title="Carregar planilha"
                            >
                                {uploading ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />} 
                                <span className="hidden sm:inline">Importar</span>
                            </button>

                            <button 
                                onClick={() => handleOpenModal()}
                                className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-white bg-blue-600 rounded-md hover:bg-blue-700 shadow-sm transition-colors"
                            >
                                <Plus size={18} /> Novo Serviço
                            </button>
                        </>
                    )}
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center p-12"><Loader2 className="animate-spin text-blue-600" size={32} /></div>
            ) : error ? (
                <div className="p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-md">
                    <p className="font-bold">Erro</p>
                    <p>{error}</p>
                </div>
            ) : (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-gray-50 border-b border-gray-200 text-xs uppercase text-gray-500 font-semibold">
                                <tr>
                                    <th className="px-6 py-3 w-1/3">Nome do Serviço</th>
                                    <th className="px-4 py-3 w-32">Tipo</th>
                                    <th className="px-4 py-3">Descrição</th>
                                    <th className="px-4 py-3 w-40 text-right">Valor ({selectedCurrency})</th>
                                    <th className="px-4 py-3 text-center w-24">Ações</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 text-sm text-gray-700">
                                {filteredServices.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="p-12 text-center text-gray-400 flex flex-col items-center justify-center">
                                            <FileSpreadsheet className="text-gray-200 mb-3" size={48} />
                                            <p className="text-lg font-medium text-gray-500">Nenhum serviço encontrado.</p>
                                        </td>
                                    </tr>
                                ) : (
                                    filteredServices.map((service) => (
                                        <tr key={service.id} className="hover:bg-blue-50/50 transition-colors group">
                                            <td className="px-6 py-4 font-medium text-gray-900">{service.name}</td>
                                            <td className="px-4 py-4">
                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                                    service.type === 'Vacina' ? 'bg-blue-100 text-blue-700' : 
                                                    service.type === 'Tratamento Dentário' ? 'bg-purple-100 text-purple-700' :
                                                    'bg-green-100 text-green-700'
                                                }`}>
                                                    {service.type || 'N/A'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-4 text-xs text-gray-500">{service.description || '-'}</td>
                                            <td className="px-4 py-4 text-right font-bold text-blue-600">
                                                {formatCurrency(service.prices?.[selectedCurrency] || 0, selectedCurrency)}
                                            </td>
                                            <td className="px-4 py-4">
                                                <div className="flex justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    {canEdit && (
                                                        <button 
                                                            onClick={() => handleOpenModal(service)}
                                                            className="p-1.5 text-blue-600 hover:bg-blue-100 rounded-md transition-colors"
                                                            title="Editar"
                                                        >
                                                            <Edit size={16} />
                                                        </button>
                                                    )}
                                                    {canDelete && (
                                                        <button 
                                                            onClick={() => handleDelete(service.id)}
                                                            className="p-1.5 text-red-600 hover:bg-red-100 rounded-md transition-colors"
                                                            title="Excluir"
                                                        >
                                                            <Trash2 size={16} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Modal de Cadastro/Edição */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg transform transition-all flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-between items-center px-6 py-4 border-b">
                            <h2 className="text-lg font-bold text-gray-800">
                                {editingService ? 'Editar Serviço Pet' : 'Novo Serviço Pet'}
                            </h2>
                            <button onClick={handleCloseModal} className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-1.5 rounded-full transition-colors">
                                <X size={20} />
                            </button>
                        </div>
                        
                        <form onSubmit={handleSave} className="flex-1 overflow-y-auto p-6 space-y-5">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Nome do Serviço <span className="text-red-500">*</span></label>
                                <input 
                                    type="text" 
                                    className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                                    value={formData.name}
                                    onChange={e => setFormData({...formData, name: e.target.value})}
                                    required
                                    placeholder="Ex: Vacina V10"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                                <select 
                                    className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                                    value={formData.type}
                                    onChange={e => setFormData({...formData, type: e.target.value})}
                                >
                                    <option value="Vacina">Vacina</option>
                                    <option value="Banho">Banho</option>
                                    <option value="Banho e Tosa">Banho e Tosa</option>
                                    <option value="Tratamento Dentário">Tratamento Dentário</option>
                                </select>
                            </div>

                            {/* --- SEÇÃO DE VALORES POR MOEDA --- */}
                            <div className="bg-blue-50/50 p-4 rounded-lg border border-blue-100">
                                <h3 className="text-xs font-bold text-blue-700 uppercase tracking-wider mb-3 flex items-center gap-2">
                                    <DollarSign size={14} /> Definição de Preços
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {allCurrencies.map(currency => (
                                        <div key={currency.code}>
                                            <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">
                                                Valor em {currency.code} ({currency.symbol})
                                            </label>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                    <span className="text-gray-400 text-sm">{currency.symbol}</span>
                                                </div>
                                                <input 
                                                    type="number" 
                                                    step="0.01"
                                                    className="w-full pl-8 p-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 outline-none"
                                                    value={formData.prices[currency.code] || ''}
                                                    onChange={e => handlePriceChange(currency.code, e.target.value)}
                                                    placeholder="0,00"
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Descrição do Serviço</label>
                                <textarea 
                                    className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all h-24 resize-none"
                                    value={formData.description}
                                    onChange={e => setFormData({...formData, description: e.target.value})}
                                    placeholder="Detalhes sobre o serviço..."
                                />
                            </div>
                        </form>

                        <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
                            <button 
                                type="button" 
                                onClick={handleCloseModal}
                                className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-100 transition-colors shadow-sm"
                            >
                                Cancelar
                            </button>
                            <button 
                                type="button" 
                                onClick={handleSave} 
                                disabled={saving}
                                className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-white bg-blue-600 rounded-md hover:bg-blue-700 shadow-md disabled:bg-blue-400 transition-colors"
                            >
                                {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
                                Salvar Serviço
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PetServiceTablePage;